import { useMemo } from "react";
import { usePackages } from "./use-packages";
import { useCategories } from "./use-categories";
import type { Product } from "../api/packages/types";
import type { Category, SubCategory } from "../api/categories/types";
import type { PackagesResponse } from "../api/packages/types";

type Section = {
    category: Category;
    products: Product[];
    subCategories: SubCategory[];
};

export const useCategorizedPackages = () => {
    const { data: packages, isLoading, error } = usePackages();
    const { data: categoriesData } = useCategories();

    const categoriesArray = useMemo<Category[]>(() => {
        if (Array.isArray(categoriesData)) {
            return categoriesData;
        }
        return [];
    }, [categoriesData]);

    const productsByCatId = useMemo<Record<string, Product[]>>(() => {
        const idx: Record<string, Product[]> = {};
        const list = packages?.data ?? [];
        for (const p of list) {
            const key = String(p.category_id ?? "uncategorized");
            (idx[key] ??= []).push(p);
        }
        return idx;
    }, [packages]);

    const topCategories = useMemo(() => {
        const filteredCategories = categoriesArray.filter(
            (c) => c.parent_id == null && c.hide_category !== true
        );
        return filteredCategories.sort((a, b) => (a.position ?? 9999) - (b.position ?? 9999));
    }, [categoriesArray]);

    const sections: Section[] = useMemo(() => {
        return topCategories.map((cat) => {
            const subCategories = Array.isArray(cat.sub_categories)
                ? cat.sub_categories.filter(
                    (s) => s.hide_category !== true && s.hide_subcategory !== true
                )
                : [];

            const subIds = subCategories.map((s) => String(s.id));
            const allIds = new Set<string>([String(cat.id), ...subIds]);

            const merged: Product[] = [];
            allIds.forEach((id) => {
                const arr = productsByCatId[id];
                if (arr?.length) merged.push(...arr);
            });

            merged.sort((a, b) => (a.position ?? 9999) - (b.position ?? 9999));

            return {
                category: cat,
                products: merged,
                subCategories,
            };
        });
    }, [topCategories, productsByCatId]);

    const sectionsWithProducts = useMemo(
        () => sections.filter((s) => s.products.length > 0 || s.subCategories.length > 0),
        [sections]
    );

    const packagesResponse: PackagesResponse = useMemo(() => {
        const productsArray = sectionsWithProducts.flatMap((section) => section.products);
        return {
            data: productsArray,
            meta: {
                total: productsArray.length,
                per_page: 12,
                current_page: 1,
                last_page: Math.ceil(productsArray.length / 12),
                first_page: 1,
                first_page_url: "",
                last_page_url: "",
                next_page_url: 2 <= Math.ceil(productsArray.length / 12) ? "next" : null,
                previous_page_url: null,
            },
        };
    }, [sectionsWithProducts]);

    return {
        isLoading,
        error,
        categories: topCategories,
        categorizedPackages: packagesResponse,
        sections: sectionsWithProducts,
    };
};
